<template>
  <div id="tags-view-container" ref="scrollPane" class="lots-tags-view tags-container">
    <div v-if="isShowTabs" class="all-tags-inner">
      <router-link
        v-for="(tag, index) in showingTags"
        :ref="el => { if (el) tagRefs[index] = el }"
        :key="tag.fullPath"
        :class="[
          'tags-view-item-all',
          isActive(tag) ? 'active' : '',
          tag.affix !== true ? 'has-close' : ''
        ]"
        :to="{ query: tag.query, path: tag.path }"
        :style="{ 
          background: isActive(tag) ? '#fff' : 'rgba(255,255,255, 0.56)',
          paddingRight: tag.affix !== true ? ((isActive(tag) || tag.isHover) ? '32px' : '16px') : '16px'
        }"
        @mouseenter="tag.isHover = true"
        @mouseleave="tag.isHover = false"
      >
        <span @click.middle="closeSelectedTag(tag)">
          <span
            class="tags-view-item"
            :draggable="true"
            @dragstart="handleItemDragStart($event, tag)"
            @dragend="handleItemDragEnd($event)"
            @dragenter="handleItemDragEnter($event, tag, index)"
          >
            <span v-if="isActive(tag)" class="tags-view-name" :style="{ maxWidth: 'auto' }">
              {{ $translate(`lang.${tag.resourceCode}`, { defaultText: tag.resourceName ?? getTranslate(tag) })
              }}{{ tag.orderNo }}
            </span>
            <span v-else :class="['tags-view-name', tag.isHover ? 'active-tag' : 'not-active-tag']">
              {{ $translate(`lang.${tag.resourceCode}`, { defaultText: tag.resourceName ?? getTranslate(tag) })
              }}{{ tag.orderNo }}
            </span>
            <md-icon v-if="tag.affix !== true" class="md-icon-custom-close">
              <close @click.prevent.stop="closeSelectedTag(tag)" />
            </md-icon>
          </span>
        </span>
      </router-link>
    </div>
    <md-dropdown
      v-if="hidingTags.length > 0"
      trigger="click"
      popper-class="more-dropdown-box"
      @visible-change="dropdownChange"
    >
      <div class="more-dropdown-btn">
        <md-icon :class="[isRotate ? 'active-rotate' : 'not-active-rotate']"><arrow-down /></md-icon>
      </div>
      <template #dropdown>
        <md-dropdown-menu>
          <md-dropdown-item
            v-for="(item, index) in [...closeAllTag]"
            :key="item.id ?? index"
            @click="handleCloseAll(item, index)"
          >
            <div class="tag-item">
              {{ $translate(`lang.${item.resourceCode}`, { defaultText: item.resourceName }) }}
            </div>
          </md-dropdown-item>
          <md-divider v-if="hidingTags.length"></md-divider>
          <md-dropdown-item
            v-for="(item, index) in [...hidingTags]"
            :key="item.id ?? index"
            @click="handleCloseAll(item, index)"
          >
            <div class="tag-item" :class="isActive(item) ? 'active' : ''">
              {{ $translate(`lang.${item.resourceCode}`, { defaultText: item.resourceName }) }}
              <md-icon class="more-dropdown-box-icon">
                <close @click.prevent.stop="closeSelectedTag(item)" />
              </md-icon>
            </div>
          </md-dropdown-item>
        </md-dropdown-menu>
      </template>
    </md-dropdown>
  </div>
</template>

<script lang="ts">
import { RouterLink } from 'vue-router';
import { MdDropdown, MdDropdownItem, MdDropdownMenu, MdIcon, MdDivider } from 'mdesign3';
import { I18nMixins, arrMove, keepAliveInstance, $translate as translate } from '@/utils';
import { lotsUIEventInstance } from '@/utils/event';
import { Close, ArrowDown } from '@mdesign3/icons-vue';
import { filter as _filter, forEach as _forEach, get as _get } from 'lodash-es';
import {
  PropType,
  computed,
  defineEmits,
  defineProps,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  Component,
} from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useResizeObserver } from '@vueuse/core';
import eventBus from '@/utils/eventBus';
import microApp from '@micro-zoe/micro-app';

export default {
  name: 'LotsTagsView',
};
</script>

<script lang="ts" setup>
type DictItem = {
  [key: string]: string;
};
interface VisitedView {
  id: string | number;
  name: string;
  frontUrl: string;
  maxWidth: number;
  query: DictItem;
  params: DictItem;
  fullPath: string;
  resourceCode: string;
  resourceName: string;
  orderNo: string;
  subResources?: VisitedView[];
  path: string;
  resourceType: number;
  redirectedFrom: string;
  compenont: Component;
  isHover?: boolean; // 添加悬停状态属性
}
const $translate = I18nMixins.methods.$translate || translate;
const props = defineProps({
  /* 是否显示语言选择器，布尔类型，默认为true */
  isShowLang: {
    type: Boolean as PropType<boolean>,
    default: true,
  },
  /* 是否显示租户信息，布尔类型，默认为true*/
  isShowTenant: {
    type: Boolean as PropType<boolean>,
    default: true,
  },
  /* 是否显示标签页，布尔类型，默认为true*/
  isShowTabs: {
    type: Boolean as PropType<boolean>,
    default: true,
  },
  /* 头部是否包含组织信息，布尔类型，默认为false*/
  hasHeaderOrg: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  /* 是否显示工具栏，布尔类型，默认为true*/
  isShowTool: {
    type: Boolean as PropType<boolean>,
    default: true,
  },
  /* 是否具有上传列表，布尔类型，默认为true*/
  hasUploadList: {
    type: Boolean as PropType<boolean>,
    default: true,
  },
  /* 记录访问过的视图，数组类型，默认为空数组*/
  visitedViews: {
    type: Array as PropType<VisitedView[]>,
    default: () => {
      return [];
    },
  },
  /* 权限设置，数组类型，默认为空数组 */
  authority: {
    type: Array as PropType<VisitedView[]>,
    default: () => {
      return [];
    },
  },
  tabWidth: {
    type: Number as PropType<number>,
    default: 68,
  },
});
interface Emits {
  /** 更新视图缓存 */
  (event: 'refresh-keep-alive', data: string | VisitedView): void;
}
const emit = defineEmits<Emits>();
const TAG_ITEM_PADDING = 16;
const TAG_ITEM_MARGIN = 4;
const TAG_ITEM_CLOSE_WIDTH = 20;
const selectedTag = ref<VisitedView>(null);
const closeAllTag = ref([
  {
    resourceName: $translate('lotsUI.E1202112140023', { defaultText: '关闭全部页签' }),
    id: 'closeAll',
    meta: {
      title: $translate('lotsUI.E1202112140023', { defaultText: '关闭全部页签' }),
    },
  },
  {
    resourceName: $translate('lotsUI.E202007300006', { defaultText: '关闭当前页签' }),
    id: 'closeCur',
    meta: {
      title: $translate('lotsUI.E202007300006', { defaultText: '关闭当前页签' }),
    },
  },
  {
    resourceName: $translate('lotsUI.E202007300006', { defaultText: '关闭其他页签' }),
    id: 'closeOthers',
    meta: {
      title: $translate('lotsUI.E202007300006', { defaultText: '关闭其他页签' }),
    },
  },
]);
// const allTags = ref<VisitedView[]>([]);
const flatResourceSearch = ref([]); // 扁平化的菜单信息
const numberCanBeShow = ref<number>(0);
// const allowCloseTag = ref<boolean>(true);
const dragElData = ref(null);
const scrollPane = ref(null);
const router = useRouter();
const route = router.currentRoute;
const store = useStore();
const showingTags = ref([]);
const hidingTags = ref([]);

const visitedViewsList = computed(() => {
  return props.visitedViews.length
    ? props.visitedViews.map((item) => {
        // @ts-ignore 需要维持引用一致性，故只能赋值
        item.getDisplayWidth = () => {
          return handleTitleMaxWidth(item.resourceName ?? getTranslate(item));
        };
        return item;
      })
    : [];
});

// 拖拽钩子
const handleItemDragStart = (ev: DragEvent, item: VisitedView) => {
  dragElData.value = item;
};
const handleItemDragEnd = (ev: DragEvent) => {
  if (ev.preventDefault) {
    ev.preventDefault();
  }
  dragElData.value = null;
};
const handleItemDragEnter = (ev: DragEvent, item: VisitedView, index: number) => {
  const currentIndex = showingTags.value.findIndex((i) => {
    return i.frontUrl === dragElData.value.frontUrl;
  });
  if (item.frontUrl !== dragElData.value.frontUrl) {
    const tempArr = arrMove(showingTags.value, currentIndex, index);
    COVER_VISITED_VIEW([...tempArr, ...hidingTags.value]);
  }
};
// 判断是否中英文来显示内容的最大宽度
const handleTitleMaxWidth = (resourceName: string) => {
  /** 计算显示高度 */
  function calculateDisplayLength(str: string, fontSize: number) {
    if (!str) return 0;
    return (
      [...str].reduce((a, b) => {
        const charCode = b.charCodeAt(0);
        // 如果字符代码大于255，则认为是中文或其他宽字符
        if (charCode > 255) {
          a += 1; // 中文字符或全角字符
        } else {
          a += 0.7; // 英文字符或半角字符
        }
        return a;
      }, 0) * fontSize
    );
  }
  return Math.min(props.tabWidth, calculateDisplayLength(resourceName, 14));
};

const isRotate = ref(false);
const dropdownChange = (val: boolean) => {
  isRotate.value = val;
};

// 添加标签引用数组
const tagRefs = ref([]);

// 修改计算可显示标签数量的函数
const handleNumberCanBe = () => {
  if (!props.visitedViews?.length) return 0;
  
  const widthCanBeUse = scrollPane.value?.offsetWidth - 28; // 减去更多按钮的宽度
  let totalWidth = 0;
  let size = 0;
  
  // 从最新的标签开始计算（从右向左）
  for (let i = visitedViewsList.value.length - 1; i >= 0; i--) {
    const tag = visitedViewsList.value[i];
    const tagWidth = tag.getDisplayWidth() + TAG_ITEM_PADDING * 2 + TAG_ITEM_MARGIN + TAG_ITEM_CLOSE_WIDTH;
    
    if (totalWidth + tagWidth < widthCanBeUse) {
      totalWidth += tagWidth;
      size++;
    } else {
      break;
    }
  }
  
  // 确保至少显示一个标签
  return Math.max(1, size);
};

const isActive = (newRoute: VisitedView) => {
  return newRoute.fullPath.split('?')[0] === route.value.fullPath.split('?')[0];
};
const getFlatResource = (resource: VisitedView[], deep = 0) => {
  for (let i = 0; i < resource.length; i++) {
    const ignoreType = 3;
    if (resource[i].resourceType !== ignoreType) {
      flatResourceSearch.value.push(resource[i]);
    }

    if (resource[i].subResources) {
      if (resource[i].subResources.length >= 0) {
        const nextDeep = deep + 1;
        getFlatResource(resource[i].subResources, nextDeep);
      }
    }
  }
};
const addViewTags = () => {
  const { name } = route.value;
  // 根据路径找到菜单信息
  let targetMenu = {};
  const findTargetMenu = flatResourceSearch.value.find((item) => {
    return item.frontUrl === route.value.path;
  });
  targetMenu = findTargetMenu || {};
  const temp = props.visitedViews.some((item) => {
    return item.name === name;
  });
  selectedTag.value = { ...targetMenu, ...route.value };
  if (name) {
    store.dispatch('addView', { ...targetMenu, ...route.value });
    numberCanBeShow.value = handleNumberCanBe();
    if (props.visitedViews.length > numberCanBeShow.value && !temp) {
      let tempArr = [];
      tempArr = arrMove([...props.visitedViews], props.visitedViews.length - 1, numberCanBeShow.value);
      tempArr = arrMove(tempArr, 0, tempArr.length - 1);
      COVER_VISITED_VIEW(tempArr);
    }
  }
  return false;
};

// 控制页面缓存
const handlePageCache = (view: VisitedView) => {
  const compName = _get(view, 'name');
  if (compName) {
    const delay = 50;
    keepAliveInstance.removeKeepAlivePage(compName);
    setTimeout(() => {
      keepAliveInstance.insertKeepAlivePage(compName);
    }, delay);
  }
};
// const setAllowCloseTag = (val: boolean) => {
//   allowCloseTag.value = !!val;
// };
const closeSelectedTag = (view: VisitedView) => {
  if (view?.id && view?.id === 'closeAll') {
    closeAllTags();
    emit('refresh-keep-alive', 'closeAll');
    return false;
  }
  // 注掉无用代码，且容易引起内存问题
  // lotsUIEventInstance.listen(view.name, setAllowCloseTag);
  // if (!allowCloseTag.value) {
  //   // 业务不允许关闭
  //   allowCloseTag.value = true; // 重置标识
  //   return false;
  // }
  emit('refresh-keep-alive', view);
  store.dispatch('delView', view).then(({ visitedViews }) => {
    handlePageCache(view);
    if (isActive(view)) {
      const latestView = visitedViews.pop();
      if (latestView) {
        router.push(latestView.fullPath);
      } else if (checkRouter(view)) {
        router.replace({
          path: `/${view.fullPath}`,
          query: {
            routeName: view.name,
          },
        });
      } else {
        router.push('/index');
      }
    }
    // allTags.value = closeAllTag.value.concat(visitedViews.slice(numberCanBeShow.value));
  });
  // 触发子应用关闭标签页
  const microActiveApps = microApp.getActiveApps();
  microActiveApps.forEach((app) => {
    microApp.setData(app, { type: 'closeSelectedTag', view });
  });
};

// 子应用触发父应用关闭标签页
const onCloseSelectedTag = (fullPath: any) => {
  const view = visitedViewsList.value.find((item) => item.fullPath === fullPath);
  if (view) {
    closeSelectedTag(view);
  }
};
eventBus.$on('onCloseSelectedTag', onCloseSelectedTag);

const checkRouter = (view: VisitedView) => {
  if (view.fullPath === '/index') {
    return true;
  } else if (view.redirectedFrom && view.redirectedFrom.matched.find((item) => item.redirect === view.fullPath)) {
    return true;
  } else {
    return false;
  }
};
const closeOthersTags = () => {
  // 不在选中的标签页时跳转
  if (route.value.name !== selectedTag.value.name) {
    router.push(selectedTag.value);
  }
  // 控制页面缓存
  _filter(props.visitedViews, (o) => selectedTag.value.name !== o.name).forEach((view) => {
    handlePageCache(view);
  });
  store.dispatch('delOthersViews', selectedTag.value);
};
const closeAllTags = () => {
  _forEach(props.visitedViews, (view) => {
    // 控制页面缓存
    handlePageCache(view);
  });
  store.dispatch('delAllViews');
  router.push('/index');
};
const handleCloseAll = (item: VisitedView, index: number) => {
  if (item.id && item.id === 'closeAll') {
    closeAllTags();
    emit('refresh-keep-alive', 'closeAll');
  } else if (item.id && item.id === 'closeOthers') {
    closeOthersTags();
  } else if (item.id && item.id === 'closeCur') {
    closeSelectedTag(route.value);
  } else {
    let tempArr = [];
    tempArr = arrMove([...props.visitedViews], index + numberCanBeShow.value - 1, numberCanBeShow.value);
    tempArr = arrMove(tempArr, 0, tempArr.length - 1);
    COVER_VISITED_VIEW(tempArr);
    router.push({ path: item.path, query: item.query, fullPath: item.fullPath });
  }
};

// 修改标签分配逻辑
const changeTags = (arr: Array<any>) => {
  // 重置标签引用数组
  tagRefs.value = [];
  
  if (arr.length <= numberCanBeShow.value) {
    // 如果所有标签都能显示，直接显示全部
    showingTags.value = [...arr];
    hidingTags.value = [];
    return;
  }
  
  // 找到当前活动标签的索引
  const activeIndex = arr.findIndex(tag => isActive(tag));
  
  // 默认显示最新的标签（从右侧开始）
  let tagsToShow = arr.slice(arr.length - numberCanBeShow.value);
  
  // 如果活动标签不在显示范围内，需要调整显示的标签
  if (activeIndex !== -1 && !tagsToShow.some(tag => tag.fullPath === arr[activeIndex].fullPath)) {
    // 确保活动标签在显示范围内
    // 优先保留最新的标签，但必须包含活动标签
    
    // 如果活动标签是较早的标签
    if (activeIndex < arr.length - numberCanBeShow.value) {
      // 显示活动标签和它右侧的标签
      tagsToShow = [
        arr[activeIndex],
        ...arr.slice(arr.length - (numberCanBeShow.value - 1))
      ];
    }
  }
  
  showingTags.value = tagsToShow;
  
  // 计算隐藏的标签
  const showingFullPaths = tagsToShow.map(tag => tag.fullPath);
  hidingTags.value = arr.filter(tag => !showingFullPaths.includes(tag.fullPath));
};

const {
  COVER_VISITED_VIEW: [COVER_VISITED_VIEW],
} = store._mutations;

const setFrom = (from: any) => {
  const fromRoute = { ...from };
  if (['microDataReport', 'microOms', 'microTms'].includes(from.name)) {
    if (fromRoute.fullPath) fromRoute.fullPath = fromRoute.fullPath.replace(`/${from.name}`, '');
    if (fromRoute.href) fromRoute.href = fromRoute.href.replace(`/${from.name}`, '');
    if (fromRoute.path) fromRoute.path = fromRoute.path.replace(`/${from.name}`, '');
  }
  return fromRoute;
};

watch(
  () => props.authority,
  (val) => {
    if (!val.length) {
      // 没有菜单权限tab栏关闭所有页面去到404页面
      closeAllTags();
    } else {
      getFlatResource(props.authority);
    }
  },
  {
    immediate: true,
    deep: true,
  },
);
watch(route, (val, from) => {
  const microActiveApps = microApp.getAllApps();
  microActiveApps.forEach((app) => {
    microApp.setData(app, { type: 'updateRouteFrom', from: setFrom(from) });
  });

  addViewTags();
});

watch(
  visitedViewsList,
  (newValue, oldVal) => {
    nextTick(() => {
      if (!dragElData.value) {
        numberCanBeShow.value = handleNumberCanBe();
        setTimeout(() => {
          changeTags(newValue);
        }, 100);
      }
    });
  },
  { immediate: true },
);

watch(numberCanBeShow, (newValue, oldVal) => {
  nextTick(() => {
    changeTags(visitedViewsList.value);
  });
});

// 优化 ResizeObserver 回调
const { stop } = useResizeObserver(scrollPane, (entries) => {
  nextTick(() => {
    numberCanBeShow.value = handleNumberCanBe();
    changeTags(visitedViewsList.value);
  });
});

// 监听路由变化
watch(route, () => {
  nextTick(() => {
    // 重新计算可显示标签数量
    numberCanBeShow.value = handleNumberCanBe();
    // 重新分配标签
    changeTags(visitedViewsList.value);
  });
});

// 监听标签列表变化
watch(visitedViewsList, () => {
  nextTick(() => {
    if (!dragElData.value) {
      numberCanBeShow.value = handleNumberCanBe();
      changeTags(visitedViewsList.value);
    }
  });
}, { immediate: true });

onBeforeUnmount(() => {
  stop();
});

onMounted(() => {
  addViewTags();

  nextTick(() => {
    // 仅处理数据资源中已标记 affix 的菜单（与原逻辑一致，不改写 affix 语义）
    // 登录后只展开其中一个：优先 isIndex=true，否则取第一个，避免多标签拖慢首屏
    const affixMenus = flatResourceSearch.value.filter((item: any) => item.affix);
    const homeMenu =
      affixMenus.find(
        (item: any) => item.extendAttr1 && item.extendAttr1.indexOf('isIndex=true') > -1,
      ) || affixMenus[0];

    if (!homeMenu?.frontUrl) {
      return;
    }

    const route = {
      fullPath: homeMenu.frontUrl,
      path: homeMenu.frontUrl,
      href: `#${homeMenu.frontUrl}`,
      meta: {},
      query: {},
      params: {},
      matched: [],
      name: homeMenu.frontUrl.split('/').filter((e: any) => e)[0],
      isRoute: false,
    };
    // 保持 { ...item, ...route }，由 processRoleResources 解析的 item.affix 决定是否不可关闭
    store.dispatch('addView', { ...homeMenu, ...route });
  });
});
const getTranslate = (tag: any) => {
  let content = null;
  router.getRoutes().forEach((item) => {
    if (item.path === tag.path) {
      content = item.name;
    }
  });
  if (!content) content = tag.title || '404';
  return content;
};
</script>
<style lang="less">
.lots-tags-view.tags-container {
  display: flex;
  flex: 1;
  overflow-x: visible;
  overflow-y: hidden;
  .all-tags-inner {
    height: 24px;
    overflow: hidden;
    display: flex;
    flex-wrap: wrap;
    flex: 1;
    justify-content: flex-start;
    align-items: center;
    gap: 8px;
  }
  .more-dropdown {
    padding: 0 8px;
    cursor: pointer;
    color: #000;
    .md-icon {
      width: 24px;
      height: 24px;
      background-color: #fff;
      border-radius: 4px;
      svg {
        transition: all 0.3s ease-in-out;
      }

      &:hover {
        svg {
          transform: rotate(180deg);
        }
      }
    }
  }
  .contextmenu {
    margin: 0;
    background: #1e2028;
    z-index: 100;
    top: 40px;
    position: fixed;
    list-style-type: none;
    line-height: 1.5em;
    font-size: 14px;
    padding: 10px 0;
    border-radius: 4px;
    font-weight: 400;
    color: #fff;

    li {
      margin: 0;
      padding: 9px 40px 9px 20px;
      cursor: pointer;
      &:hover {
        background: #000;
      }
    }
  }
  .tags-view-item-all {
    cursor: pointer;
    display: inline-block;
    position: relative; /* 确保相对定位，作为绝对定位的参考 */
    line-height: 24px;
    padding: 0 16px;
    font-size: 14px;
    color: #4e5969;
    text-decoration: none !important;
    border-radius: 4px;

    &.active {
      color: #4285f5;
      
      &:after {
        content: none;
      }
      .tags-view-item {
        border: none;
        top: 3px;
        text-decoration: none;
      }
    }
    
    &.has-close {
      transition: padding-right 0.3s ease;
      
      &.active,
      &:hover {
        padding-right: 32px;
        color: #4285f5;
        
        .md-icon-custom-close {
          opacity: 1;
          transform: translateY(-50%) translateX(0);
        }
      }
      
      .md-icon-custom-close {
        position: absolute;
        right: 8px;
        top: 50%;
        transform: translateY(-50%) translateX(10px);
        opacity: 0;
        transition: opacity 0.3s ease, transform 0.3s ease;
        z-index: 1; /* 确保图标在最上层 */
        cursor: pointer;
        color: #4e5969;
        
        &:hover {
          color: #4285f5;
        }
      }
    }
  }

  .tags-view-item {
    display: flex;
    justify-content: center;
    align-items: center;
    outline: none;
  }
  .tags-view-name {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    display: inline-block;
  }
  .more-dropdown-btn {
    width: 24px;
    height: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #fff;
    border-radius: 4px;
    cursor: pointer;
  }
  .active-rotate {
    color: #1d2129;
    transform: rotate(180deg);
    transition: all 0.3s ease-in-out;
  }
  .not-active-rotate {
    color: #1d2129;
    transform: rotate(0deg);
    transition: all 0.3s ease-in-out;
  }
  .active-tag {
    max-width: none !important; /* 悬停时不限制宽度 */
    transition: max-width 0.3s ease;
    white-space: nowrap;
  }
  .not-active-tag {
    max-width: 100px;
    transition: max-width 0.3s ease;
  }
}
.md-popper.more-dropdown-box[role='tooltip'] {
  width: 188px;
  margin-top: -5px;
  .md-popper__arrow {
    &:before {
      width: 0;
      height: 0;
    }
  }
  .md-dropdown-menu {
    width: 188px;
    max-height: 400px;
    padding: 4px 0;
    overflow: auto;
    background: #ffffff;
    overflow-x: hidden;

    .popper__arrow {
      display: none;
    }
    font-size: 0;
    line-height: 0;
    .md-dropdown-menu__item {
      display: inline-block;
      width: 146px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      color: #1d2129;
      font-size: 14px;
      position: relative;
      padding: 0 30px 0 12px;
      line-height: 32px;
      margin-bottom: 4px;
      .more-dropdown-box-icon {
        display: none;
        width: 14px;
        height: 14px;
        position: absolute;
        right: 5px;
        top: 9px;
        color: #4e5969;
      }
      &:hover {
        background: none;
        .tag-item::before {
          background: #f2f3f5;
        }
        color: #1d2129;
        .more-dropdown-box-icon {
          display: block;
        }
      }
      .tag-item {
        &::before {
          width: 100%;
          height: 100%;
          content: '';
          position: absolute;
          left: 0;
          bottom: 0;
          z-index: -1;
        }
        &.active {
          color: var(--sidebar-main-color, #2878ff);
          &::before {
            background-color: var(--sidebar-main-bg, #2878ff);
          }
        }
      }
    }
    .md-divider--horizontal {
      width: auto;
      margin: 4px 12px;
    }
    a:first-child {
      .tag-item {
        color: #ffffff;
        font-weight: bold;
      }
    }
  }
}
</style>
